import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useStockManagement } from '@/hooks/useStockManagement';
import { sendOrderConfirmationEmail, sendSellerNotificationEmail } from '@/lib/actions/emailActions';
import RelatedPurchases from '@/components/home/RelatedPurchases';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ShoppingCart, AlertTriangle } from 'lucide-react';

// Define the form schema
const purchaseFormSchema = z.object({
  fullName: z.string().min(3, { message: 'Ju lutem shkruani emrin e plotë' }),
  phoneNumber: z.string().min(8, { message: 'Ju lutem shkruani një numër telefoni të vlefshëm' }),
  address: z.string().min(5, { message: 'Ju lutem shkruani adresën e plotë' }),
  city: z.string().min(2, { message: 'Ju lutem zgjidhni një qytet' }),
  postalCode: z.string().optional(),
  notes: z.string().optional(),
  email: z.string().email({ message: 'Ju lutem shkruani një email të vlefshëm' }),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: 'Ju duhet të pranoni kushtet para se të vazhdoni',
  }),
});

type PurchaseFormValues = z.infer<typeof purchaseFormSchema>;

interface PurchaseOrderFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number;
  productTitle: string;
  productImage?: string;
  price: number;
  currency: string;
  sellerId: string;
}

export function PurchaseOrderForm({
  open,
  onOpenChange,
  productId,
  productTitle,
  productImage,
  price,
  currency,
  sellerId,
}: PurchaseOrderFormProps) {
  
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleDecrementStock } = useStockManagement();

  // Add unique ID for dialog descriptions
  const dialogDescriptionId = React.useId();

  // Add query to get user profile information including email
  const { data: userProfile } = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', user.id)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });
  
  // Form with default values
  const form = useForm<PurchaseFormValues>({
    resolver: zodResolver(purchaseFormSchema),
    defaultValues: {
      fullName: '',
      phoneNumber: '',
      address: '',
      city: '',
      postalCode: '',
      notes: '',
      email: userProfile?.email || '', // Pre-fill email if available
      termsAccepted: false,
    },
  });
  
  // Update email field when userProfile changes
  useEffect(() => {
    if (userProfile?.email) {
      form.setValue('email', userProfile.email);
    }
  }, [userProfile, form]);
  
  // Check if user is logged in before showing the form
  useEffect(() => {
    if (open && !user) {
      onOpenChange(false);
      toast({
        title: "Login i nevojshëm",
        description: "Ju duhet të jeni të loguar për të bërë një porosi.",
        variant: "destructive"
      });
      navigate('/auth');
    }
  }, [open, user, navigate, onOpenChange, toast]);

  // Check if the current user is the seller
  useEffect(() => {
    if (open && user && user.id === sellerId) {
      onOpenChange(false);
      toast({
        title: "Blerje e padisponueshme",
        description: "Nuk mund të blini produktin tuaj. Ju lutem kontrolloni produktet e tjera.",
        variant: "destructive"
      });
    }
  }, [open, user, sellerId, onOpenChange, toast]);
  
  // Fetch user's previous delivery information if available
  const { isLoading: isLoadingUserInfo } = useQuery({
    queryKey: ['userDeliveryInfo', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data: orders, error } = await supabase
        .from('orders')
        .select('delivery_address')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error) throw error;
      
      if (orders && orders.length > 0 && orders[0].delivery_address) {
        try {
          const addressInfo = JSON.parse(orders[0].delivery_address);
          form.reset({
            fullName: addressInfo.fullName || '',
            phoneNumber: addressInfo.phoneNumber || '',
            address: addressInfo.address || '',
            city: addressInfo.city || '',
            postalCode: addressInfo.postalCode || '',
            notes: '',
            email: userProfile?.email || '',
            termsAccepted: false,
          });
        } catch (e) {
          console.error('Error parsing stored address', e);
        }
      }
      
      return orders;
    },
    enabled: !!user && open && user.id !== sellerId, // Only run if not own product
  });
  
  // Update create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (values: PurchaseFormValues) => {
      const buyerEmail = userProfile?.email || values.email;

      if (!user) throw new Error('Ju duhet të jeni të loguar për të bërë porosinë');
      if (!buyerEmail) throw new Error('Ju duhet të jepni një adresë emaili');

      // Prevent buying own products
      if (user.id === sellerId) {
        throw new Error('Nuk mund të blini produktin tuaj');
      }
      
      // First, check the current stock
      const { data: listing, error: listingError } = await supabase
        .from('listings')
        .select('stock, user_id')
        .eq('id', productId)
        .single();
      
      if (listingError) throw listingError;
      
      if (!listing || listing.stock <= 0) {
        throw new Error('Produkti nuk është më në gjendje');
      }
      
      if (listing.user_id === user.id) {
        throw new Error('Nuk mund të blini produktin tuaj');
      }
      
      try {
        const deliveryAddress = JSON.stringify({
          fullName: values.fullName,
          phoneNumber: values.phoneNumber,
          address: values.address,
          city: values.city,
          postalCode: values.postalCode,
        });

        const { data: order, error: orderError } = await supabase
          .from('orders')
          .insert({
            user_id: user.id,
            seller_id: sellerId,
            listing_id: productId,
            amount: price,
            currency,
            delivery_address: deliveryAddress,
            shipping_method: 'standard',
            status: 'pending',
            buyer_email: buyerEmail, // Use email
          })
          .select()
          .single();
        
        if (orderError) throw orderError;

        try {
          const success = await handleDecrementStock(productId, 1);
          if (!success) console.error('Error updating stock');
        } catch (error) {
          console.error('Exception during stock update:', error);
        }
        
        return order;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: async (order) => {
      toast({
        title: 'Porosia u krijua me sukses',
        description: 'Shitësi do t\'ju kontaktojë së shpejti për të finalizuar porosinë.',
      });
      
      try {
        const { data: sellerData } = await supabase
          .from('profiles')
          .select('email, username')
          .eq('id', sellerId)
          .single();
          
        if (sellerData) {
          const enrichedOrderDetails = {
            ...order,
            buyer_email: userProfile?.email,
            seller_name: sellerData.username || 'Seller',
            seller_email: sellerData.email,
            listing_title: productTitle,
          };
          
          await sendOrderConfirmationEmail(enrichedOrderDetails)
            .catch(error => {
              console.error('Failed to send buyer confirmation email:', error);
              toast({
                title: 'Gabim',
                description: 'Dërgimi i emailit të konfirmimit dështoi, por porosia u krijua me sukses.',
                variant: 'destructive',
              });
            });
            
          await sendSellerNotificationEmail(enrichedOrderDetails)
            .catch(error => console.error('Failed to send seller notification email:', error));
        }
      } catch (error) {
        console.error('Error fetching seller info for emails:', error);
      }
      
      queryClient.invalidateQueries({ queryKey: ['userPurchases'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] }); 
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Gabim gjatë krijimit të porosisë',
        description: error.message || 'Ju lutemi provoni përsëri më vonë.',
        variant: 'destructive',
      });
    },
  });
  
  function onSubmit(values: PurchaseFormValues) {
    createOrderMutation.mutate(values);
  }
  
  if (user && user.id === sellerId) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" aria-describedby={dialogDescriptionId}>
        <DialogHeader>
          <DialogTitle className="text-xl">Konfirmoni porosinë tuaj</DialogTitle>
          <DialogDescription id={dialogDescriptionId}>
            Ju lutemi plotësoni detajet tuaja për të finalizuar porosinë
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center p-3 bg-gray-50 rounded-md mb-4">
          <div className="w-16 h-16 rounded overflow-hidden mr-3 bg-gray-200 flex-shrink-0">
            {productImage ? (
              <img src={productImage} alt={productTitle} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-500">
                No image
              </div>
            )}
          </div>
          <div>
            <h3 className="font-medium">{productTitle}</h3>
            <p className="text-green-600 font-medium">
              {price} {currency}
            </p>
          </div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Vëmendje:</strong> Platforma jonë nuk është përgjegjëse për transaksionet dhe nuk ofron mbrojtje 
              për blerjet. Pagesa duhet të bëhet vetëm pas marrjes së produktit. Ju lutemi komunikoni drejtpërdrejt me 
              shitësin për detaje të mëtejshme.
            </p>
          </div>
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emri i plotë</FormLabel>
                  <FormControl>
                    <Input placeholder="Emri juaj i plotë" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Numri i telefonit</FormLabel>
                  <FormControl>
                    <Input placeholder="Numri i telefonit" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adresa</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Adresa e dorëzimit" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Qyteti</FormLabel>
                    <FormControl>
                      <Input placeholder="Qyteti" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kodi postar (opsional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Kodi postar" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Shënime shtesë (opsional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Shënime për shitësin" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Adresa juaj e emailit" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Jam dakord me kushtet e blerjes dhe kuptoj që pagesa do të bëhet vetëm pas marrjes së produktit
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={createOrderMutation.isPending}
              >
                Anulo
              </Button>
              <Button 
                type="submit"
                disabled={createOrderMutation.isPending || isLoadingUserInfo}
              >
                {createOrderMutation.isPending ? (
                  <span className="flex items-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Duke u procesuar...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Konfirmo porosinë
                  </span>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Export a function to show related products after purchase
export function RelatedProductsAfterPurchase({ productId }: { productId: number }) {
  return (
    <div className="mt-6">
      <RelatedPurchases
        title="Klientët Blenë Gjithashtu"
        listingId={productId}
        limit={4}
      />
    </div>
  );
}
